import React, { useRef, useState, lazy, Suspense } from "react";

import { courts } from "../data/courts.js";
import { brand } from "../theme/brand.js";
import { NovoLogo } from "../theme/logo.jsx";
import { btnSecondary } from "../theme/button-styles.js";
import { useAuth } from "../hooks/useAuth.js";
import { useHistoryState } from "../hooks/useHistoryState.js";
import { useOrg } from "../hooks/useOrg.js";

// Eager: shown on first paint (org picker / service menu / login gate).
import { ServiceMenu } from "./ServiceMenu.jsx";
import { OrgPickerScreen } from "./OrgPicker.jsx";
import { Login } from "./Login.jsx";
import { SetPassword } from "./SetPassword.jsx";
import { AUTH_LINK_TYPE } from "../lib/supabase.js";

// Lazy: each service module is its own chunk, loaded only when its view
// opens. This keeps the document generators (pdf-lib / docxtemplater) and
// their base64 PDF templates (ar-11 ~215KB, eoir-33 ~203KB) out of the
// initial bundle. The barrels export named components, so map .default.
const named = (loader, key) => lazy(() => loader().then((m) => ({ default: m[key] })));

const IRSTranscriptModule = named(() => import("../modules/irs-transcript"), "IRSTranscriptModule");
const DetaineeLocatorModule = named(() => import("../modules/detainee-locator"), "DetaineeLocatorModule");
const N400MailingModule = named(() => import("../modules/n400-mailing"), "N400MailingModule");
const HearingLookupModule = named(() => import("../modules/hearing-lookup"), "HearingLookupModule");
const CaseStatusModule = named(() => import("../modules/case-status"), "CaseStatusModule");
const AddressLookupModule = named(() => import("../modules/address-lookup"), "AddressLookupModule");
const AR11Module = named(() => import("../modules/ar-11"), "AR11Module");
const EOIR33Module = named(() => import("../modules/eoir-33"), "EOIR33Module");
const ChangeVenueModule = named(() => import("../modules/change-venue"), "ChangeVenueModule");
const ComboCoaModule = named(() => import("../modules/combo-coa"), "ComboCoaModule");
const CivicsFlashcardsModule = named(() => import("../modules/civics-flashcards"), "CivicsFlashcardsModule");
const ReadingWritingModule = named(() => import("../modules/vocab-flashcards"), "ReadingWritingModule");
const NatScreenerModule = named(() => import("../modules/nat-screener"), "NatScreenerModule");
const EnglishExemptionModule = named(() => import("../modules/english-exemption"), "EnglishExemptionModule");
const N400FeesModule = named(() => import("../modules/n400-fees"), "N400FeesModule");
const LettersOfSupportModule = named(() => import("../modules/letters-of-support"), "LettersOfSupportModule");
const N400WorkshopModule = named(() => import("../modules/n400-workshop"), "N400WorkshopModule");
const N400BuilderModule = named(() => import("../modules/n400-builder"), "N400BuilderModule");
const I589BuilderModule = named(() => import("../modules/i589-builder"), "I589BuilderModule");
const I589WorkshopModule = named(() => import("../modules/i589-workshop"), "I589WorkshopModule");
const RaidResponseModule = named(() => import("../modules/raid-response"), "RaidResponseModule");
const PreRaidModule = named(() => import("../modules/pre-raid"), "PreRaidModule");
const AdminPanel = named(() => import("../modules/admin/AdminPanel.jsx"), "AdminPanel");

function LoadingView() {
  return (
    <>
      <style>{`
        @keyframes novoBarSweep {
          from { transform: translateX(-100%); }
          to   { transform: translateX(500%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .novo-loading-bar-fill { animation: none !important; width: 100% !important; opacity: 0.35; }
        }
      `}</style>
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: "3px", overflow: "hidden", zIndex: 1000, backgroundColor: brand.porcelainBlue }}>
        <div
          className="novo-loading-bar-fill"
          style={{
            width: "25%",
            height: "100%",
            backgroundColor: brand.prussianBlue,
            borderRadius: "2px",
            animation: "novoBarSweep 1.1s ease-in-out infinite",
          }}
        />
      </div>
      <div style={{ padding: "64px 24px", textAlign: "center", color: brand.textSecondary, fontFamily: brand.font, fontSize: "14px" }}>
        Loading…
      </div>
    </>
  );
}

// Every top-level screen, as it appears after the # in the address bar.
const VIEWS = new Set([
  "services", "login", "admin", "ar-11", "eoir-33", "change-venue", "case-status", "address-lookup",
  "hearing-lookup", "n400-mailing", "nat-screener", "irs-transcript", "english-exemption", "n400-fees",
  "detainee-locator", "civics-flashcards", "vocab-flashcards", "letters-of-support", "n400-builder",
  "i589-builder", "i589-workshop", "pre-raid", "raid-response", "n400-workshop", "combo-coa",
]);
const viewHash = (s) => s.v;

// A refresh reopens the screen named in the address bar.
function viewFromAddress() {
  const v = typeof window !== "undefined" ? window.location.hash.replace(/^#\/?/, "") : "";
  return VIEWS.has(v) ? v : "services";
}

export function NovoNavigator() {
  const { orgId: activeOrgId, org: activeOrg, setOrgId } = useOrg();
  // Each screen change is a browser-history step: the browser's Back button
  // returns to the previous screen instead of leaving Navigator.
  const [screen, go] = useHistoryState("view", { v: viewFromAddress() }, { hash: viewHash });
  const view = screen.v;
  const setView = (v, opts) => { if (v !== view || opts?.replace) go({ v, from: opts?.replace ? screen.from : view }, opts); };
  // In-app "Back to services": step back in history when that is where this
  // screen was opened from, so Back and Forward stay in step with the app.
  const backToServices = () => (screen.from === "services" ? window.history.back() : setView("services"));
  const lastAppliedOrg = useRef(null);
  const { isAdmin, loading: authLoading, signOut } = useAuth();
  // Opened from an invite / password-reset email: choose a password first.
  const [passwordLink, setPasswordLink] = useState(AUTH_LINK_TYPE);

  // Apply org theme to the shared brand object
  if (activeOrgId && activeOrgId !== lastAppliedOrg.current) {
    if (activeOrgId === 'novo') {
      brand.prussianBlue = '#08314d';
      brand.porcelainBlue = '#e8f1f7';
      brand.textPrimary = '#08314d';
      brand.accentColor = '#08314d';
    } else if (activeOrg) {
      brand.prussianBlue = activeOrg.headingColor || '#08314d';
      brand.porcelainBlue = activeOrg.secondary;
      brand.textPrimary = activeOrg.headingColor || '#08314d';
      brand.accentColor = activeOrg.primary;
    }
    lastAppliedOrg.current = activeOrgId;
  }

  // Show org picker if no org selected — AFTER all hooks
  if (!activeOrgId) {
    return <OrgPickerScreen onSelect={setOrgId} />;
  }

  const handleStartService = (serviceId) => {
    if (serviceId === "ar-11") setView("ar-11");
    if (serviceId === "eoir-33") setView("eoir-33");
    if (serviceId === "change-venue") setView("change-venue");
    if (serviceId === "case-status") setView("case-status");
    if (serviceId === "address-lookup") setView("address-lookup");
    if (serviceId === "hearing-lookup") setView("hearing-lookup");
    if (serviceId === "n400-mailing") setView("n400-mailing");
    if (serviceId === "nat-screener") setView("nat-screener");
    if (serviceId === "irs-transcript") setView("irs-transcript");
    if (serviceId === "english-exemption") setView("english-exemption");
    if (serviceId === "n400-fees") setView("n400-fees");
    if (serviceId === "detainee-locator") setView("detainee-locator");
    if (serviceId === "civics-flashcards") setView("civics-flashcards");
    if (serviceId === "vocab-flashcards") setView("vocab-flashcards");
    if (serviceId === "letters-of-support") setView("letters-of-support");
    if (serviceId === "n400-builder") setView("n400-builder");
    if (serviceId === "i589-builder") setView("i589-builder");
    if (serviceId === "i589-workshop") setView("i589-workshop");
    if (serviceId === "pre-raid") setView("pre-raid");
    if (serviceId === "raid-response") setView("raid-response");
    if (serviceId === "n400-workshop") setView("n400-workshop");
    if (serviceId === "combo-coa") setView("combo-coa");
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: brand.bgPage, fontFamily: brand.font }}>
      {/* Disclaimer Banner */}
      <div style={{
        backgroundColor: activeOrg ? activeOrg.secondary : brand.porcelainBlue, borderBottom: `1px solid ${brand.borderLight}`,
        padding: "10px 24px", fontSize: "13px", color: activeOrg ? activeOrg.textOnSecondary : brand.textPrimary,
        textAlign: "center", lineHeight: "1.4", fontFamily: brand.font,
      }}>
        This tool helps prepare documents. It does not provide legal advice. If you have questions about your case, please contact a licensed immigration attorney.
      </div>

      {/* Header */}
      <header style={{
        backgroundColor: brand.white, borderBottom: `1px solid ${brand.borderLight}`,
        padding: "20px 32px", display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "relative",
      }}>
        {/* Left: Always Novo logo */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px", cursor: "pointer" }} onClick={() => setView("services")}>
          <NovoLogo />
          <div style={{ width: "1px", height: "28px", backgroundColor: brand.borderLight }} />
          <div>
            <p style={{ margin: 0, fontSize: "15px", fontWeight: "600", color: brand.prussianBlue, letterSpacing: "-0.01em", fontFamily: brand.font }}>
              Navigator
            </p>
            <p style={{ margin: 0, fontSize: "11px", color: brand.textSecondary, fontWeight: "400", fontFamily: brand.font }}>
              Document Preparation Tool
            </p>
          </div>
        </div>

        {/* Center: Partner logo (if not Novo) */}
        {activeOrg && activeOrg.logo && activeOrg.id !== 'novo' && (
          <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}>
            <img src={activeOrg.logo} alt={activeOrg.abbreviation} style={{ height: activeOrg.logoHeight || '36px', width: 'auto', maxWidth: '220px', objectFit: 'contain' }} />
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {view === "admin" && (
            <span style={{ fontSize: "12px", fontWeight: "600", color: brand.prussianBlue, backgroundColor: brand.porcelainBlue, padding: "4px 10px", borderRadius: "6px" }}>
              Admin Mode
            </span>
          )}
          <button
            onClick={() => setOrgId(null)}
            style={{ padding: '6px 12px', fontSize: '11px', fontWeight: '500', fontFamily: brand.font, borderRadius: '5px', border: `1px solid ${brand.borderLight}`, backgroundColor: 'transparent', color: brand.textSecondary, cursor: 'pointer', marginRight: '6px' }}
          >
            Switch Org
          </button>
          <button
            style={btnSecondary}
            onClick={() => {
              if (view === "admin") {
                signOut();
                setView("services");
              } else {
                setView(isAdmin ? "admin" : "login");
              }
            }}
          >
            {view === "admin" ? "Exit Admin" : "Admin Dashboard"}
          </button>
        </div>
      </header>

      <Suspense fallback={<LoadingView />}>
      {passwordLink ? (
        <SetPassword
          kind={passwordLink}
          onDone={() => { setPasswordLink(null); setView("admin", { replace: true }); }}
        />
      ) : <>
      {/* Login Gate */}
      {/* Also shown when the address bar or Back lands on admin while signed out */}
      {(view === "login" || (view === "admin" && !isAdmin && !authLoading)) && (
        <Login
          onSuccess={() => setView("admin", { replace: true })}
          onCancel={backToServices}
        />
      )}

      {/* Service Menu */}
      {view === "services" && <ServiceMenu onStartService={handleStartService} />}

      {/* Module A — AR-11 Change of Address (USCIS) */}
      {view === "ar-11" && (
        <AR11Module onBack={backToServices} />
      )}

      {/* Module B — EOIR-33 Change of Address */}
      {view === "eoir-33" && (
        <EOIR33Module
          courts={courts}
          onBack={backToServices}
        />
      )}

      {/* Module C — Change Venue */}
      {view === "change-venue" && (
        <ChangeVenueModule
          courts={courts}
          onBack={backToServices}
          onLaunchService={(id) => handleStartService(id)}
        />
      )}

      {/* Module D — USCIS Case Status Checker */}
      {view === "case-status" && (
        <CaseStatusModule onBack={backToServices} />
      )}

      {/* Module F — Court & OPLA Address Lookup */}
      {view === "address-lookup" && (
        <AddressLookupModule
          courts={courts}
          onBack={backToServices}
        />
      )}

      {/* Module E — Hearing Date & Court Lookup */}
      {view === "hearing-lookup" && (
        <HearingLookupModule onBack={backToServices} />
      )}

      {/* Module G — N-400 Mailing Address Lookup */}
      {view === "n400-mailing" && (
        <N400MailingModule onBack={backToServices} />
      )}

      {/* Module H — Naturalization Eligibility Screener */}
      {view === "nat-screener" && (
        <NatScreenerModule onBack={backToServices} />
      )}

      {/* Module I — IRS Tax Transcript Request */}
      {view === "irs-transcript" && (
        <IRSTranscriptModule onBack={backToServices} />
      )}

      {/* English Exemption Calculator */}
      {view === "english-exemption" && (
        <EnglishExemptionModule onBack={backToServices} />
      )}

      {/* N-400 Filing Fees */}
      {view === "n400-fees" && (
        <N400FeesModule onBack={backToServices} />
      )}

      {/* ICE Detainee Locator */}
      {view === "detainee-locator" && (
        <DetaineeLocatorModule onBack={backToServices} />
      )}

      {/* Civics Flashcards */}
      {view === "civics-flashcards" && (
        <CivicsFlashcardsModule onBack={backToServices} language="en" />
      )}

      {/* Reading & Writing Test */}
      {view === "vocab-flashcards" && (
        <ReadingWritingModule onBack={backToServices} language="en" />
      )}

      {/* Letters of Support Guide */}
      {view === "letters-of-support" && (
        <LettersOfSupportModule onBack={backToServices} />
      )}

      {/* Combo Change of Address */}
      {view === "combo-coa" && (
        <ComboCoaModule courts={courts} onBack={backToServices} />
      )}

      {/* N-400 Workshop */}
      {view === "n400-workshop" && (
        <N400WorkshopModule onBack={backToServices} BuilderComponent={N400BuilderModule} />
      )}

      {/* N-400 Builder */}
      {view === "n400-builder" && (
        <N400BuilderModule onBack={backToServices} />
      )}

      {/* I-589 Builder */}
      {view === "i589-builder" && (
        <I589BuilderModule onBack={backToServices} />
      )}

      {/* Asylum Workshop */}
      {view === "i589-workshop" && (
        <I589WorkshopModule onBack={backToServices} BuilderComponent={I589BuilderModule} />
      )}

      {/* Raid Response Module */}
      {view === "pre-raid" && (
        <PreRaidModule onBack={backToServices} />
      )}

      {view === "raid-response" && (
        <RaidResponseModule onBack={backToServices} />
      )}

      {/* Admin Dashboard */}
      {view === "admin" && isAdmin && <AdminPanel onBack={() => { signOut(); setView("services"); }} />}
      </>}
      </Suspense>
    </div>
  );
}
